// src/routes/AppRoutes.jsx

// UTILITIES
import { lazy } from 'react';
import { Navigate, Route, Routes, useLocation, useParams } from 'react-router-dom';
import ProtectedRoute from './ProtectedRoute';
import { useAuth } from "../auth/authContext";
import { useFacilitatorCapabilities } from "../features/facilitated-surfaces/context/FacilitatorCapabilityContext";
import { ApplicationButton, ApplicationStateRegion, ApplicationSurface } from "../components/application";

// LAYOUTS
import WebLayout from '../layouts/WebLayout';
import AppLayout from '../layouts/AppLayout';
import MobileOrientationGate from '../layouts/MobileOrientationGate';

///////////////
///// WEB /////
///////////////

// SIGN UP & LOGIN & VERIFICATIONS
import SignUpPage from '../auth/components/SignUpPage';
import EmailVerification from '../auth/components/EmailVerification';
import PasswordResetComponent from '../auth/components/PasswordResetComponent';
import LoginPage from '../auth/components/LoginPage';
import CertificateVerification from "../web/CertificateVerification";

// EXPLORE
import Landing from '../web/Landing';
import InsightsList from "../web/insights/InsightsList";
import InsightArticle from "../web/insights/InsightArticle";

// SERVICES
import ServicesPage from '../web/services/ServicesPage';
import PerceptualSkillDevelopment from '../web/services/PerceptualSkillDevelopment';
import InstitutionalPrograms from '../web/services/InstitutionalPrograms';
import PerceptualResearchInfrastructure from '../web/services/PerceptualResearchInfrastructure';
import PerceptualDataAnnotation from '../web/services/PerceptualDataAnnotation';
import AccessAndPricing from '../web/services/AccessAndPricing';

// ABOUT
import AboutUs from '../web/about/AboutUs';
import ManifestoPage from '../web/about/ManifestoPage';
import Collaborations from '../web/about/Collaborations';
import Acknowledgements from '../web/about/Acknowledgements';
import CreditsPage from '../web/about/CreditsPage';

// LEGAL
import LegalNotice from '../web/legal/LegalNotice';
import PrivacyPolicy from '../web/legal/PrivacyPolicy';
import TermsAndConditions from '../web/legal/TermsAndConditions';
import TermsAcceptanceGate from '../auth/legal/TermsAcceptanceGate';
import AccountClosurePage from '../features/account-lifecycle/AccountClosurePage';

// CONNECT
import ConnectPage from '../web/connect/ConnectPage';
import ContactPage from '../web/connect/ContactPage';
import NewsletterPage from '../web/connect/NewsletterPage';
import NewsletterConfirm from '../web/connect/NewsletterConfirm';
import MeetingPage from '../web/connect/MeetingPage';

// EVENTS (legacy?)
import WVDEvent from '../activities/wvd/WVDEvent';
import WVDParticipate from '../activities/wvd/WVDParticipate';
import ASHAConventionAnchors from '../activities/anchors/ASHAConventionAnchors';
import JoinAnchors from '../activities/anchors/JoinAnchors';

///////////////
///// APP /////
///////////////

// NAVIGATION
import AppDashboard from '../layouts/components/AppDashboard';
import DynamicAppView from '../layouts/components/DynamicAppView';
import AppCatalogEntryDetailPage from "../catalog/pages/AppCatalogEntryDetailPage";
import AppCatalogResourceGuidePage from "../catalog/pages/AppCatalogResourceGuidePage";
import {
  canonicalPathwayRouteForLegacyViewId
} from "../catalog/launch/catalogPathwayCompatibility";

// PROJECTS (legacy?)
import ProjectsHub from "../features/projects/views/ProjectsHub";
import ProjectDashboard from "../features/projects/views/ProjectDashboard";
import ProjectFlowEntry from "../features/projects/views/ProjectFlowEntry";
import ProjectFacilitatorOverview from "../features/projects/views/ProjectFacilitatorOverview";
import ProjectFacilitatorMembers from "../features/projects/views/ProjectFacilitatorMembers";
import ProjectFacilitatorCohortResults from "../features/projects/views/ProjectFacilitatorCohortResults";
import ProjectFacilitatorExports from "../features/projects/views/ProjectFacilitatorExports";
import ProjectFacilitatorMemberDetail from "../features/projects/views/ProjectFacilitatorMemberDetail";
import DurableFacilitatedSessionsList from "../features/durable-facilitated-sessions/views/DurableFacilitatedSessionsList";
import DurableFacilitatedSessionDetail from "../features/durable-facilitated-sessions/views/DurableFacilitatedSessionDetail";
import AssignedWorkList from "../features/assignments/views/AssignedWorkList";
import AssignedWorkDetail from "../features/assignments/views/AssignedWorkDetail";
import AssignedEvaluationRuntimePage from "../features/assignments/views/AssignedEvaluationRuntimePage";
import FacilitatorAssignmentsPage from "../features/assignments/views/FacilitatorAssignmentsPage";
import FacilitatorAssignmentDetail from "../features/assignments/views/FacilitatorAssignmentDetail";
import CreateAssignmentPage from "../features/assignments/views/CreateAssignmentPage";
import ParticipatePage from "../features/facilitated-surfaces/pages/ParticipatePage";
import FacilitatePage from "../features/facilitated-surfaces/pages/FacilitatePage";
import FacilitateLiveChooserPage from "../features/facilitated-surfaces/pages/FacilitateLiveChooserPage";
import ActivityLibraryPage from "../features/activity-authoring/pages/ActivityLibraryPage";
import ActivityDetailPage from "../features/activity-authoring/pages/ActivityDetailPage";
import ActivityCreatePage from "../features/activity-authoring/pages/ActivityCreatePage";
import ActivityComposerPage from "../features/activity-authoring/pages/ActivityComposerPage";

const ProjectAccessProjectsPage = lazy(() => import(
  "../admin/projects/ProjectAccessAdministration"
).then(module => ({ default: module.ProjectAccessProjectsPage })));
const ProjectAccessProjectPage = lazy(() => import(
  "../admin/projects/ProjectAccessAdministration"
).then(module => ({ default: module.ProjectAccessProjectPage })));
const CommunityExperienceModerationQueuePage = lazy(() => import(
  "../admin/community-experience/CommunityExperienceAdministration"
).then(module => ({ default: module.CommunityExperienceModerationQueuePage })));
const CommunityExperienceModerationDetailPage = lazy(() => import(
  "../admin/community-experience/CommunityExperienceAdministration"
).then(module => ({ default: module.CommunityExperienceModerationDetailPage })));

function FacilitatorCapabilityRoute({
  capability,
  children,
  redirectTo,
}) {
  const { loadingUser } = useAuth();
  const capabilityState = useFacilitatorCapabilities();
  const allowed = capability === "activity.author"
    ? capabilityState.canAuthorActivities
    : capabilityState.canManageAssignments;
  const decision = capability === "activity.author"
    ? capabilityState.activityAuthorDecision
    : capabilityState.assignmentManageDecision;

  if (loadingUser || capabilityState.status === "idle" || capabilityState.status === "loading") {
    return <ApplicationStateRegion status="loading" message="Checking access…" />;
  }
  if (capabilityState.status === "unavailable" || decision?.effect === "indeterminate") {
    return <ApplicationSurface><ApplicationStateRegion status="error" message="Access could not be checked. Please try again." /><ApplicationButton onClick={capabilityState.refresh}>Retry</ApplicationButton></ApplicationSurface>;
  }
  return allowed ? children : <Navigate replace to={redirectTo} />;
}

function CatalogPathwayCompatibilityRedirect({ legacyViewId }) {
  const location = useLocation();
  const params = useParams();
  const resolvedLegacyViewId =
    legacyViewId || params.viewId;
  const canonicalPathwayRoute =
    canonicalPathwayRouteForLegacyViewId(resolvedLegacyViewId);

  if (!canonicalPathwayRoute) {
    return <DynamicAppView />;
  }

  const [pathname, search = ""] =
    canonicalPathwayRoute.split("?");
  const mergedSearchParams =
    new URLSearchParams(location.search);

  if (search) {
    const compatibilityParams =
      new URLSearchParams(search);

    compatibilityParams.forEach((value, key) => {
      mergedSearchParams.set(key, value);
    });
  }

  return (
    <Navigate
      replace
      to={{
        pathname,
        search: mergedSearchParams.toString()
          ? `?${mergedSearchParams.toString()}`
          : "",
        hash: location.hash
      }}
    />
  );
}

const AppRoutes = () => {
  
  return (
      <Routes>
        <Route path="/account-closure" element={<AccountClosurePage />} />

        {/* 🌐 WEBLAYOUT */}
        <Route element={<WebLayout />}>

          {/* SIGN UP & LOGIN & VERIFICATIONS */}
          <Route path="/signup" element={<SignUpPage />} />
          <Route path="/verify-email" element={<EmailVerification />} />
          <Route path="/password-reset/:token" element={<PasswordResetComponent />} />
          <Route path="/login" element={<LoginPage />} />
          <Route path="/verify/:certificateId" element={<CertificateVerification />} />

          {/* EXPLORE */}
          <Route path="/" element={<Landing />} />
          <Route path="/insights" element={<InsightsList />} /> 
          <Route path="/insights/:slug" element={<InsightArticle />} /> 

          {/* SERVICES */}
          <Route path="/services" element={<ServicesPage />} /> 
          <Route path="/perceptual-skill-development" element={<PerceptualSkillDevelopment />} />
          <Route path="/perceptual-research-infrastructure" element={<PerceptualResearchInfrastructure />} />
          <Route path="/perceptual-data-annotation" element={<PerceptualDataAnnotation />} />
          <Route path="/institutional-programs" element={<InstitutionalPrograms />} />
          <Route path="/access-and-pricing" element={<AccessAndPricing />} />

          {/* ABOUT */}
          <Route path="/about" element={<AboutUs />} />
          <Route path="/listening-revolution" element={<ManifestoPage />} /> 
          <Route path="/collaborations" element={<Collaborations />} />
          <Route path="/acknowledgements" element={<Acknowledgements />} />
          <Route path="/credits" element={<CreditsPage />} />

          {/* LEGAL */}
          <Route path="/legal-notice" element={<LegalNotice />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/terms-and-conditions" element={<TermsAndConditions />} />

          {/* CONNECT */}
          <Route path="/connect" element={<ConnectPage />} />
          <Route path="/contact" element={<ContactPage />} />
          <Route path="/newsletter" element={<NewsletterPage />} />
          <Route path="/newsletter/confirm" element={<NewsletterConfirm />} />
          <Route path="/meet" element={<MeetingPage />} />

          {/* EVENTS (legacy?) */}
          <Route path="/wvd-event" element={<WVDEvent />} />
          <Route path="/wvd-participate" element={<WVDParticipate />} />
          <Route path="/2024-asha-convention-anchors" element={<ASHAConventionAnchors />} />
          <Route path="/join-anchors" element={<JoinAnchors />} />

        </Route>




        {/* APP LAYOUT */}
        <Route
          path="/app"
          element={
            <ProtectedRoute
              allowedRoles={[
                "standard_user",
                "admin"
              ]}
            >
              <TermsAcceptanceGate>
                <MobileOrientationGate>
                  <AppLayout />
                </MobileOrientationGate>
              </TermsAcceptanceGate>
            </ProtectedRoute>
          }
        >
          {/* DASHBOARD */}
          <Route index element={<AppDashboard />} />

          {/* CATALOG */}
          <Route
            path="guide"
            element={<AppCatalogResourceGuidePage />}
          />
          <Route
            path="catalog/resource-guide"
            element={
              <Navigate replace to="/app/guide" />
            }
          />
          <Route
            path="catalog/:slug"
            element={<AppCatalogEntryDetailPage />}
          />

          {/* PROJECTS (legacy?) */}
          <Route path="projects" element={<ProjectsHub />} />
          <Route path="admin/projects" element={<ProtectedRoute allowedRoles={['admin']}><ProjectAccessProjectsPage /></ProtectedRoute>} />
          <Route path="admin/projects/:projectId" element={<ProtectedRoute allowedRoles={['admin']}><ProjectAccessProjectPage /></ProtectedRoute>} />
          <Route path="admin/community-experiences" element={<ProtectedRoute allowedRoles={['admin']}><CommunityExperienceModerationQueuePage /></ProtectedRoute>} />
          <Route path="admin/community-experiences/:contributionId" element={<ProtectedRoute allowedRoles={['admin']}><CommunityExperienceModerationDetailPage /></ProtectedRoute>} />
          <Route path="project/:slug" element={<ProjectDashboard />} />
          <Route
            path="project/:slug/run/:programSlug"
            element={<ProjectFlowEntry />}
          />
          <Route
            path="durable-facilitated-sessions"
            element={<DurableFacilitatedSessionsList />}
          />
          <Route
            path="durable-facilitated-sessions/:durableSessionId"
            element={<DurableFacilitatedSessionDetail />}
          />
          <Route
            path="assigned-work"
            element={<AssignedWorkList />}
          />
          <Route
            path="assigned-work/:assignmentId"
            element={<AssignedWorkDetail />}
          />
          <Route
            path="assigned-work/:assignmentId/run"
            element={<AssignedEvaluationRuntimePage />}
          />
          <Route
            path="participate"
            element={<ParticipatePage />}
          />
          <Route
            path="facilitate"
            element={<FacilitatePage />}
          />
          <Route
            path="facilitate/live"
            element={<FacilitateLiveChooserPage />}
          />
          <Route
            path="facilitate/activities"
            element={<FacilitatorCapabilityRoute capability="activity.author" redirectTo="/app/facilitate"><ActivityLibraryPage /></FacilitatorCapabilityRoute>}
          />
          <Route
            path="facilitate/activities/new"
            element={<FacilitatorCapabilityRoute capability="activity.author" redirectTo="/app/facilitate"><ActivityCreatePage /></FacilitatorCapabilityRoute>}
          />
          <Route
            path="facilitate/activities/new/:authoringFamily"
            element={<FacilitatorCapabilityRoute capability="activity.author" redirectTo="/app/facilitate"><ActivityComposerPage /></FacilitatorCapabilityRoute>}
          />
          <Route
            path="facilitate/activities/:activityId/edit"
            element={<FacilitatorCapabilityRoute capability="activity.author" redirectTo="/app/facilitate"><ActivityComposerPage /></FacilitatorCapabilityRoute>}
          />
          <Route
            path="facilitate/activities/:activityId"
            element={<FacilitatorCapabilityRoute capability="activity.author" redirectTo="/app/facilitate"><ActivityDetailPage /></FacilitatorCapabilityRoute>}
          />
          <Route
            path="facilitate/project/:slug"
            element={<ProjectFacilitatorOverview />}
          />
          <Route
            path="facilitate/project/:slug/members"
            element={<ProjectFacilitatorMembers />}
          />
          <Route
            path="facilitate/project/:slug/cohort-results"
            element={<ProjectFacilitatorCohortResults />}
          />
          <Route
            path="facilitate/project/:slug/exports"
            element={<ProjectFacilitatorExports />}
          />
          <Route
            path="facilitate/project/:slug/member/:memberRef"
            element={<ProjectFacilitatorMemberDetail />}
          />
          <Route
            path="assignments"
            element={
              <FacilitatorCapabilityRoute capability="assignment.manage"
                redirectTo="/app/facilitate"
              >
                <FacilitatorAssignmentsPage />
              </FacilitatorCapabilityRoute>
            }
          />
          <Route
            path="assignments/:assignmentId"
            element={
              <FacilitatorCapabilityRoute capability="assignment.manage"
                redirectTo="/app/facilitate"
              >
                <FacilitatorAssignmentDetail />
              </FacilitatorCapabilityRoute>
            }
          />
          <Route
            path="assignments/new"
            element={
              <FacilitatorCapabilityRoute capability="assignment.manage" redirectTo="/app/facilitate">
                <CreateAssignmentPage />
              </FacilitatorCapabilityRoute>
            }
          />

          {/* CATALOG-LAUNCHED INTERNAL SURFACES */}
          <Route path=":slug/open" element={<DynamicAppView />} />

          {/* GENERIC APP VIEWS */}
          <Route
            path=":viewId"
            element={<CatalogPathwayCompatibilityRedirect />}
          />
        </Route>
      </Routes>
  );
};

export default AppRoutes;
